import { useState } from "react";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { Navigate, useNavigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";

const LOGO_SRC = "/brand/pazapa-logo.jpg";

export default function AdminLogin() {
  const { user, login, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-muted">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Erreur de connexion.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted grid place-items-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <img src={LOGO_SRC} alt="" className="w-14 h-14 rounded-full object-cover" />
          <div>
            <div className="font-heading text-2xl text-primary leading-none">PAZAPA</div>
            <div className="text-xs uppercase tracking-[0.2em] text-foreground/60 mt-1">
              Espace administrateur
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-3xl p-8 shadow-[0_20px_60px_-30px_rgba(23,49,58,0.4)] border border-border"
          data-testid="admin-login-form"
        >
          <div className="w-12 h-12 rounded-2xl bg-primary/10 grid place-items-center text-primary">
            <Lock className="w-5 h-5" strokeWidth={2.2} />
          </div>
          <h1 className="mt-4 font-heading text-2xl">Se connecter</h1>
          <p className="mt-1 text-sm text-foreground/70">
            Espace réservé à l&apos;équipe PAZAPA.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-bold">E-mail</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                placeholder="admin@pazapa.be"
                className="mt-2 w-full px-4 py-3 rounded-2xl border-2 border-border bg-white outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-bold">Mot de passe</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="mt-2 w-full px-4 py-3 rounded-2xl border-2 border-border bg-white outline-none focus:border-primary"
              />
            </div>
            {error && (
              <div
                className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-xl px-3 py-2"
                data-testid="admin-login-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit"
              className="pill-btn bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 w-full justify-center"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Se connecter
            </button>
          </div>
        </form>
        <div className="mt-6 text-center">
          <a href="/" className="text-sm font-semibold text-foreground/70 hover:text-primary link-underline">
            ← Retour au site
          </a>
        </div>
      </div>
    </div>
  );
}
